import { BoxGeometry, BufferAttribute, Euler, Float32BufferAttribute, Vector2, Vector3, Vector4 } from "three";
import { DoubleArray, Model, QuadArray, TextureAsset } from "./model/Model";
import { ModelElement, ModelFaces } from "./model/ModelElement";
import { CUBE_FACES, CubeFace } from "./CubeFace";
import { ModelTextures } from "./assets/ModelTextures";
import { Assets } from "./assets/Assets";
import { Maybe, toRadians } from "./util/util";
import { WrappedImage } from "./WrappedImage";
import { CanvasImage } from "./canvas/CanvasImage";
import { TextureAtlas } from "./texture/TextureAtlas";
import { Caching } from "./cache/Caching";
import { Textures } from "./texture/Textures";
import { AnimatorFunction } from "./AnimatorFunction";
import { MinecraftTextureMeta } from "./MinecraftTextureMeta";
import { AssetKey } from "./assets/AssetKey";
import { ModelGenerator } from "./model/ModelGenerator";
import { prefix } from "./util/log";

const p = prefix("UVMapper");

export const DEFAULT_UV: QuadArray = [0, 0, 16, 16];
const X = 0;
const Y = 1;
const Z = 2;

// Each face's right/down axes in Minecraft's top-origin UV coordinates.
const FACE_UV_AXES: Record<CubeFace, [Vector3, Vector3]> = {
    east: [new Vector3(0, 0, -1), new Vector3(0, -1, 0)],
    west: [new Vector3(0, 0, 1), new Vector3(0, -1, 0)],
    up: [new Vector3(1, 0, 0), new Vector3(0, 0, 1)],
    down: [new Vector3(1, 0, 0), new Vector3(0, 0, -1)],
    south: [new Vector3(1, 0, 0), new Vector3(0, -1, 0)],
    north: [new Vector3(-1, 0, 0), new Vector3(0, -1, 0)]
};

/*
 * - Minecraft Block UVs are from the top-left to bottom-right
 * - three.js UVs are from the bottom-left to top-right
 * - canvas coordinates are from the top-left to bottom-right
 */

// noinspection PointlessArithmeticExpressionJS
/** Maps Minecraft face coordinates to Three.js UVs and builds per-model texture atlases. */
export class UVMapper {

    protected static makeUv(uvCoord: number, originalTextureSize: number, actualTextureSize: number): number {
        if (uvCoord === 0) return 0;
        return (actualTextureSize / originalTextureSize) * (uvCoord / originalTextureSize);
    }

    protected static makeUvCoords([originalTextureWidth, originalTextureHeight]: DoubleArray, [actualTextureWidth, actualTextureHeight]: DoubleArray, [x1, y1, x2, y2]: QuadArray): QuadArray {
        // Flip from top-to-bottom coordinates to bottom-to-top ones
        y1 = originalTextureHeight - y1;
        y2 = originalTextureHeight - y2;

        const u1 = this.makeUv(x1, originalTextureWidth, actualTextureWidth);
        const v1 = this.makeUv(y1, originalTextureHeight, actualTextureHeight);
        const u2 = this.makeUv(x2, originalTextureWidth, actualTextureWidth);
        const v2 = this.makeUv(y2, originalTextureHeight, actualTextureHeight);

        return [u1, v1, u2, v2];
    }


    public static rotateSingleUv([u, v]: DoubleArray, [px, py]: DoubleArray, radians: number): DoubleArray {
        const sin = Math.sin(radians);
        const cos = Math.cos(radians);

        u -= px;
        v -= py;

        const uc = u;
        const vc = v;

        u = cos * uc - sin * vc;
        v = cos * vc + sin * uc;

        u += px;
        v += py;

        // return [parseFloat(u.toFixed(8)), parseFloat(v.toFixed(8))];
        return [u, v];
    }

    public static addCubeFaceUvToArray(array: number[], faceIndex: number, originalTextureSize: DoubleArray, actualTextureSize: DoubleArray, originalCoords: QuadArray) {
        const uv = this.makeUvCoords(originalTextureSize, actualTextureSize, originalCoords);
        this.setCubeFaceUvInArray(array, faceIndex, uv);
    }

    public static setCubeFaceUvInArray(array: number[], faceIndex: number, [u1, v1, u2, v2]: QuadArray) {
        let a: DoubleArray = [u1, v1]; // top left
        let b: DoubleArray = [u2, v1]; // top right
        let c: DoubleArray = [u1, v2]; // bottom left
        let d: DoubleArray = [u2, v2]; // bottom right

        let uvs: QuadArray<DoubleArray> = [a, b, c, d];

        this.setFaceUvInArray(array, faceIndex, uvs);
    }

    public static setFaceUvInArrayV(array: number[], faceIndex: number, uvs: QuadArray<Vector2>) {
        array[((faceIndex + 0) * 2) + 0] = uvs[0].x;
        array[((faceIndex + 0) * 2) + 1] = uvs[0].y;

        array[((faceIndex + 1) * 2) + 0] = uvs[1].x;
        array[((faceIndex + 1) * 2) + 1] = uvs[1].y;

        array[((faceIndex + 2) * 2) + 0] = uvs[2].x;
        array[((faceIndex + 2) * 2) + 1] = uvs[2].y;

        array[((faceIndex + 3) * 2) + 0] = uvs[3].x;
        array[((faceIndex + 3) * 2) + 1] = uvs[3].y;
    }

    public static setFaceUvInArray(array: number[], faceIndex: number, uvs: QuadArray<DoubleArray>) {
        array[((faceIndex + 0) * 2) + 0] = uvs[0][0];
        array[((faceIndex + 0) * 2) + 1] = uvs[0][1];

        array[((faceIndex + 1) * 2) + 0] = uvs[1][0];
        array[((faceIndex + 1) * 2) + 1] = uvs[1][1];

        array[((faceIndex + 2) * 2) + 0] = uvs[2][0];
        array[((faceIndex + 2) * 2) + 1] = uvs[2][1];

        array[((faceIndex + 3) * 2) + 0] = uvs[3][0];
        array[((faceIndex + 3) * 2) + 1] = uvs[3][1];
    }

    public static setCubeFaceUvAttribute(attributes: BufferAttribute, faceIndex: number, originalTextureSize: DoubleArray, actualTextureSize: DoubleArray, originalCoords: QuadArray): void {
        const uv = this.makeUvCoords(originalTextureSize, actualTextureSize, originalCoords);
        this.setCubeFaceUvInAttribute(attributes, faceIndex, uv);
    }

    public static setCubeFaceUvInAttribute(attributes: BufferAttribute, faceIndex: number, [u1, v1, u2, v2]: QuadArray) {
        attributes.setXY(faceIndex + 0, u1, v1);
        attributes.setXY(faceIndex + 1, u2, v1);
        attributes.setXY(faceIndex + 2, u1, v2);
        attributes.setXY(faceIndex + 3, u2, v2);
    }

    public static setCubeUvs(geometry: BoxGeometry, originalTextureSize: DoubleArray, actualTextureSize: DoubleArray, faces: ModelFaces): void {
        const attributes = geometry.getAttribute("uv") as BufferAttribute;
        for (let faceIndex = 0; faceIndex < CUBE_FACES.length; faceIndex++) {
            let faceName = CUBE_FACES[faceIndex];
            let face = faces[faceName];
            if (!face) continue;
            if (!face.uv) {
                face.uv = DEFAULT_UV;
            }

            this.setCubeFaceUvAttribute(attributes, faceIndex * 4, originalTextureSize, actualTextureSize, face.uv);
        }
    }

    public static facesToUvArray(faces: ModelFaces, originalTextureSize: DoubleArray, actualTextureSize: DoubleArray): number[] {
        const array: number[] = [];
        for (let faceIndex = 0; faceIndex < CUBE_FACES.length; faceIndex++) {
            let faceName = CUBE_FACES[faceIndex];
            let face = faces[faceName];
            if (!face) continue;
            if (!face.uv) {
                face.uv = DEFAULT_UV;
            }

            this.addCubeFaceUvToArray(array, faceIndex * 4, originalTextureSize, actualTextureSize, face.uv);
        }
        return array;
    }

    public static facesToUv(faces: ModelFaces, originalTextureSize: DoubleArray, actualTextureSize: DoubleArray): Float32BufferAttribute {
        return new Float32BufferAttribute(this.facesToUvArray(faces, originalTextureSize, actualTextureSize), 2);
    }

    /** Bounds texture sampling to texel centres without changing cropped face UVs. */
    public static setAtlasUvBounds(geometry: BoxGeometry, faces: ModelFaces, atlas: TextureAtlas): void {
        const uv = geometry.getAttribute("uv");
        const bounds = new Float32Array(uv.count * 4);
        for (const [faceIndex, faceName] of CUBE_FACES.entries()) {
            const texture = faces[faceName]?.texture?.substring(1);
            const position = texture && atlas.positions[texture];
            const size = texture && atlas.sizes[texture];
            let minU = Infinity, minV = Infinity, maxU = -Infinity, maxV = -Infinity;
            if (position && size) {
                minU = position[0] / atlas.image.width;
                maxU = (position[0] + size[0]) / atlas.image.width;
                minV = 1 - (position[1] + size[1]) / atlas.image.height;
                maxV = 1 - position[1] / atlas.image.height;
            } else {
                for (let corner = 0; corner < 4; corner++) {
                    const index = faceIndex * 4 + corner;
                    minU = Math.min(minU, uv.getX(index));
                    minV = Math.min(minV, uv.getY(index));
                    maxU = Math.max(maxU, uv.getX(index));
                    maxV = Math.max(maxV, uv.getY(index));
                }
            }
            const insetU = Math.min(0.5 / atlas.image.width, (maxU - minU) / 2);
            const insetV = Math.min(0.5 / atlas.image.height, (maxV - minV) / 2);
            for (let corner = 0; corner < 4; corner++) {
                bounds.set([minU + insetU, minV + insetV, maxU - insetU, maxV - insetV], (faceIndex * 4 + corner) * 4);
            }
        }
        geometry.setAttribute("uvBounds", new Float32BufferAttribute(bounds, 4));
    }

    public static lockUvs(geometry: BoxGeometry, faces: ModelFaces, atlas: TextureAtlas, rotation: Euler): void {
        const uv = geometry.getAttribute("uv") as BufferAttribute;
        for (let faceIndex = 0; faceIndex < CUBE_FACES.length; faceIndex++) {
            const faceName = CUBE_FACES[faceIndex];
            const texture = faces[faceName]?.texture?.substring(1);
            const position = texture && atlas.positions[texture];
            const size = texture && atlas.sizes[texture];
            if (!position || !size) continue;

            const [sourceU, sourceV] = FACE_UV_AXES[faceName];
            const uAxis = sourceU.clone().applyEuler(rotation);
            const vAxis = sourceV.clone().applyEuler(rotation);
            const normal = vAxis.clone().cross(uAxis);
            const targetFace = CUBE_FACES.find(name => {
                const [u, v] = FACE_UV_AXES[name];
                return v.clone().cross(u).dot(normal) > 0.5;
            })!;
            const [targetU, targetV] = FACE_UV_AXES[targetFace];
            const uu = Math.round(targetU.dot(uAxis));
            const uvCross = Math.round(targetU.dot(vAxis));
            const vu = Math.round(targetV.dot(uAxis));
            const vv = Math.round(targetV.dot(vAxis));

            // Rotate about the full texture center, not the cropped face or atlas center.
            for (let corner = 0; corner < 4; corner++) {
                const index = faceIndex * 4 + corner;
                const u = (uv.getX(index) * atlas.image.width - position[0]) / size[0] - 0.5;
                const v = ((1 - uv.getY(index)) * atlas.image.height - position[1]) / size[1] - 0.5;
                const lockedU = uu * u + uvCross * v + 0.5;
                const lockedV = vu * u + vv * v + 0.5;
                uv.setXY(index,
                    (position[0] + lockedU * size[0]) / atlas.image.width,
                    1 - (position[1] + lockedV * size[1]) / atlas.image.height);
            }
        }
        uv.needsUpdate = true;
    }

    /** Returns the shared atlas for a keyed, merged model, creating it on first use. */
    public static async getAtlas(model: Model): Promise<Maybe<TextureAtlas>> {
        const keyStr = model.key!.serialize();
        return Caching.modelTextureAtlasCache.get(keyStr, k => {
            return this.createAtlas(model);
        })
    }

    public static fillMissingTextureKeys<T>(textures: ModelTextures, target: { [k: string]: T }): { [k: string]: T } {
        for (let faceName of CUBE_FACES) {
            let v = textures[faceName];
            if (v && v.startsWith("#")) {
                target[faceName] = target[v.substr(1)];
            }
        }
        return target;
    }

    // https://yeleha.co/3qlboGS
    protected static getFallbackUv(element: ModelElement, faceName: CubeFace): QuadArray {
        switch (faceName) {
            case CubeFace.DOWN:
                return [element.from[X], 16 - element.to[Z], element.to[X], 16 - element.from[Z]];
            case CubeFace.UP:
                return [element.from[X], element.from[Z], element.to[X], element.to[Z]];
            case CubeFace.NORTH:
            default:
                return [16 - element.to[X], 16 - element.to[Y], 16 - element.from[X], 16 - element.from[Y]];
            case CubeFace.SOUTH:
                return [element.from[X], 16 - element.to[Y], element.to[X], 16 - element.from[Y]];
            case CubeFace.WEST:
                return [element.from[Z], 16 - element.to[Y], element.to[Z], 16 - element.from[Y]];
            case CubeFace.EAST:
                return [16 - element.to[Z], 16 - element.to[Y], 16 - element.from[Z], 16 - element.from[Y]];
        }
    }

    protected static expandAndRotateMcUvs([u1, v1, u2, v2]: QuadArray, rotation: number = 0, actualWidth: number, actualHeight: number): QuadArray<Vector2> {
        // Resize coordinates to actual texture size
        const scaleU = actualWidth / 16;
        const scaleV = actualHeight / 16;
        u1 *= scaleU;
        v1 *= scaleV;
        u2 *= scaleU;
        v2 *= scaleV;

        let tl: Vector2 = new Vector2(u1, v1);// top left
        let tr: Vector2 = new Vector2(u2, v1);// top right
        let bl: Vector2 = new Vector2(u1, v2);// bottom left
        let br: Vector2 = new Vector2(u2, v2);// bottom right

        switch (rotation) {
            case 0:
            case 360:
            default:
                break;
            case 90:
                tl = new Vector2(u1, v2);
                tr = new Vector2(u1, v1);
                bl = new Vector2(u2, v2);
                br = new Vector2(u2, v1);
                break;
            case 180:
                tl = new Vector2(u2, v2);
                tr = new Vector2(u1, v2);
                bl = new Vector2(u2, v1);
                br = new Vector2(u1, v1);
                break;
            case 270:
                tl = new Vector2(u2, v1);
                tr = new Vector2(u2, v2);
                bl = new Vector2(u1, v1);
                br = new Vector2(u1, v2);
                break;
        }

        return [tl, tr, bl, br];
    }

    /**
     * Combines model textures and animation metadata into an atlas.
     * Mapped UVs are stored on copies of the model's elements, available through the returned atlas.
     * Returns `undefined` for models without a texture map.
     */
    public static async createAtlas(originalModel: Model): Promise<Maybe<TextureAtlas>> {
        const textureMap: { [key: string]: Maybe<WrappedImage>; } = {};
        const metaMap: { [key: string]: Maybe<MinecraftTextureMeta>; } = {};
        const model = {...originalModel};
        // Merged models share element objects with their cached parents (cube, cube_all, ...), so the
        // baked UVs below must go onto this atlas's own copies, not onto objects other atlases read.
        if (originalModel.elements) {
            model.elements = originalModel.elements.map(element => ({ ...element }));
        }
        const isItemModel = !("elements" in model);

        console.debug(p, "Creating Atlas for", model.key);

        if (model.textures) {
            const promises: Promise<void>[] = [];
            const uniqueTextureNames: string[] = []; // TODO: make these actually unique
            const textureReferences: { [k: string]: string; } = {};
            for (let textureKey in model.textures) {
                let textureValue = model.textures[textureKey];
                if (textureValue.startsWith("#")) {
                    textureReferences[textureKey] = textureValue.substr(1);
                } else {
                    uniqueTextureNames.push(textureKey);
                    const assetKey = AssetKey.parse("textures", textureValue, model.key);
                    promises.push(ModelTextures.get(assetKey).then(asset => {
                        if (!asset) {
                            console.warn(p, "Missing texture", assetKey)
                            return;
                        }
                        textureMap[textureKey] = new WrappedImage(asset);
                    }));
                    promises.push(ModelTextures.getMeta(assetKey).then(meta => {
                        metaMap[textureKey] = meta;
                    }))
                }
            }
            await Promise.all(promises);
            const textureCount = uniqueTextureNames.length;
            console.debug(p, "Creating Atlas for", textureCount, "textures");
            // console.log(model.textures)

            this.fillMissingTextureKeys(model.textures, textureMap);
            this.fillMissingTextureKeys(model.textures, metaMap);

            const sizes: { [texture: string]: DoubleArray; } = {};
            const frames: { [texture: string]: { index: number; time: number }[] } = {};

            // Find largest texture dimensions
            let maxWidth = 0;
            for (let textureKey of uniqueTextureNames) {
                let texture = textureMap[textureKey];
                if (!texture) continue;
                const animation = metaMap[textureKey]?.animation;
                let width = texture.width, height = texture.height;
                if (animation) {
                    const square = Math.min(width, height);
                    width = animation.width ?? (animation.height === undefined ? square : width);
                    height = animation.height ?? (animation.width === undefined ? square : height);
                    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0
                        || texture.width % width || texture.height % height) {
                        throw new RangeError(`Invalid animation frame dimensions for ${textureKey}`);
                    }
                    const count = (texture.width / width) * (texture.height / height);
                    const time = animation.frametime ?? 1;
                    frames[textureKey] = (animation.frames ?? Array.from({ length: count }, (_, index) => index))
                        .map(frame => typeof frame === "number" ? { index: frame, time } : { index: frame.index, time: frame.time ?? time });
                    if (!frames[textureKey].length || frames[textureKey].some(frame => !Number.isInteger(frame.index)
                        || frame.index < 0 || frame.index >= count || !Number.isInteger(frame.time) || frame.time < 1)) {
                        throw new RangeError(`Invalid animation frames for ${textureKey}`);
                    }
                }
                sizes[textureKey] = [width, height];
                maxWidth = Math.max(maxWidth, width, height);
            }
            this.fillMissingTextureKeys(model.textures, sizes);

            // console.log("sizes", sizes);

            const s = (Math.ceil(Math.sqrt(textureCount + 1))); // +1 for transparency
            const size = Math.ceil(s * maxWidth)
            console.debug(p, "Atlas size:", size);
            // console.log("size: " + size);
            const squaredSize = size * size;

            const image = new CanvasImage(size, size);
            const missing = Textures.getMissing().image;
            const missingPixels = image.context.createImageData(missing.width, missing.height);
            missingPixels.data.set(missing.data!);
            image.putData(missingPixels, 0, 0);
            // The top-left atlas cell keeps absent model faces transparent.
            image.context.clearRect(0, 0, maxWidth, maxWidth);

            const positions: { [texture: string]: DoubleArray; } = {};

            let hasTransparency = false;
            let hasTranslucency = false;

            let hasAnimation = false;
            const animatorFunctions: { [texture: string]: AnimatorFunction; } = {};

            // Draw all textures onto a single image
            let tx = 1; // start one over to leave room for the transparent space
            let ty = 0;
            for (let textureIndex = 0; textureIndex < uniqueTextureNames.length; textureIndex++) {
                let textureKey = uniqueTextureNames[textureIndex];
                let texture = textureMap[textureKey];
                let x = tx * maxWidth;
                let y = ty * maxWidth;
                if (texture) {
                    positions[textureKey] = [x, y];
                    const [width, height] = sizes[textureKey];
                    const sequence = frames[textureKey];
                    const getFrameData = (index: number) => {
                        const columns = texture!.width / width;
                        return texture!.getSectionData((index % columns) * width,
                            Math.floor(index / columns) * height, width, height);
                    };
                    const firstFrame = getFrameData(sequence?.[0].index ?? 0);
                    image.putData(firstFrame, x, y);

                    if (texture.hasTransparency) {
                        hasTransparency = true;
                    }
                    if (texture.hasTranslucency) {
                        hasTranslucency = true;
                    }

                    if (sequence && sequence.some(frame => frame.index !== sequence[0].index)) {
                        hasAnimation = true;
                        let t = 0;
                        let f = 0;
                        const interpolated = metaMap[textureKey]?.animation?.interpolate
                            ? image.context.createImageData(width, height) : undefined;
                        let currentFrame = firstFrame;
                        let nextFrame = interpolated ? getFrameData(sequence[1].index) : firstFrame;

                        animatorFunctions[textureKey] = () => {
                            if (++t >= sequence[f].time) {
                                t = 0;
                                const previous = sequence[f].index;
                                f = (f + 1) % sequence.length;
                                if (interpolated) {
                                    currentFrame = nextFrame;
                                    nextFrame = getFrameData(sequence[(f + 1) % sequence.length].index);
                                }
                                if (sequence[f].index === previous) return false;
                                image.putData(interpolated ? currentFrame : getFrameData(sequence[f].index), x, y);
                                return true;
                            }
                            if (!interpolated || sequence[f].index === sequence[(f + 1) % sequence.length].index) return false;
                            // Vanilla encodes the atlas shader's interpolation progress in thousandths.
                            const progress = Math.floor(Math.fround(Math.fround(t / sequence[f].time) * 1000)) / 1000;
                            for (let i = 0; i < interpolated.data.length; i++) {
                                interpolated.data[i] = currentFrame.data[i] + (nextFrame.data[i] - currentFrame.data[i]) * progress;
                            }
                            image.putData(interpolated, x, y);
                            return true;
                        };
                    }
                }
                tx++;
                if (tx >= s) {
                    tx = 0;
                    ty++;
                }
            }
            this.fillMissingTextureKeys(model.textures, positions);

            // console.log("positions", positions);

            if (isItemModel) {
                console.debug(p, "Generating item model for", model);
                model.elements = [];
                for (let layerName of ModelGenerator.ITEM_LAYERS) {
                    const textureImage = textureMap[layerName];
                    if (textureImage) {
                        // Outline the frame that was drawn into the atlas, not the whole animation strip.
                        const [width, height] = sizes[layerName];
                        const frame = frames[layerName]?.[0].index ?? 0;
                        const columns = textureImage.width / width;
                        model.elements.push(...ModelGenerator.generateItemModel(textureImage.getSectionData(
                            (frame % columns) * width, Math.floor(frame / columns) * height, width, height), layerName))
                    }
                }
                console.debug(p, "Item model elements", model.elements)
            }

            if (!model.elements) {
                //TODO: default elements
            }

            // Adjust UV positions
            if (model.elements) {
                for (let elementIndex = 0; elementIndex < model.elements.length; elementIndex++) {
                    let element = model.elements[elementIndex];
                    let uv: number[] = [];
                    // Set default uvs to missing texture
                    for (let i = 0; i < CUBE_FACES.length; i++) {
                        this.setCubeFaceUvInArray(uv, i * 4, [0, 0, 8 / size, 8 / size])
                    }
                    for (let faceIndex = 0; faceIndex < CUBE_FACES.length; faceIndex++) {
                        let faceName = CUBE_FACES[faceIndex];
                        // console.log(faceName)
                        let face = element.faces[faceName];
                        if (!face) {
                            // set uv transparent
                            this.setCubeFaceUvInArray(uv, faceIndex * 4, [0, 1, 8 / size, (size - 8) / size])
                            continue;
                        }
                        // Keep the missing-texture UVs when no texture is referenced.
                        if (!face.texture) continue;
                        if (!face.uv) {
                            face.uv = this.getFallbackUv(element, faceName);
                        }

                        const faceTexture = face.texture.substr(1); // remove #
                        const texPosition = positions[faceTexture] || [0, 0];
                        const texSize = sizes[faceTexture] || [16, 16];

                        let texPosV = new Vector2(texPosition[0], texPosition[1]);

                        const [tl, tr, bl, br] = this.expandAndRotateMcUvs(face.uv, face.rotation, texSize[0], texSize[1]);

                        // move to atlas position
                        tl.add(texPosV);
                        tr.add(texPosV);
                        bl.add(texPosV);
                        br.add(texPosV);

                        // divide to 0-1
                        tl.divideScalar(size);
                        tr.divideScalar(size);
                        bl.divideScalar(size);
                        br.divideScalar(size);

                        // flip y coordinates
                        tl.y = 1 - tl.y;
                        tr.y = 1 - tr.y;
                        bl.y = 1 - bl.y;
                        br.y = 1 - br.y;

                        let uvs: QuadArray<Vector2> = [tl, tr, bl, br];
                        // console.log("uvs", uvs);
                        this.setFaceUvInArrayV(uv, faceIndex * 4, uvs);
                    }

                    element.mappedUv = uv;
                }
            } else {
                //TODO: item model
            }

            return new TextureAtlas(
                model,
                image,
                sizes,
                positions,
                hasAnimation,
                animatorFunctions,
                hasTransparency,
                hasTranslucency
            );
        } else {
            console.warn(p, "Model does not have any textures", model);
        }
        return undefined;
    }

}

// Based on net.minecraft.client.renderer.block.model.BlockFaceUV
/** Stores a Minecraft face's UV rectangle and its rotation in degrees. */
export class MinecraftFaceUV {

    constructor(readonly uv: QuadArray = [0, 0, 16, 16], readonly rotation: number = 0) {
    }

    getU(index: number): number {
        const shiftedIndex = this.getShiftedIndex(index);
        return this.uv[shiftedIndex !== 0 && index !== 1 ? 2 : 0];
    }

    getV(index: number): number {
        const shiftedIndex = this.getShiftedIndex(index);
        return this.uv[shiftedIndex !== 0 && index !== 3 ? 3 : 1];
    }

    protected getShiftedIndex(index: number): number {
        return (index + this.rotation / 90) % 40;
    }

    protected getReverseIndex(index: number): number {
        return (index + 4 - this.rotation / 90) % 4;
    }

}
